// Create Folder Tool - Provides directory creation functions for workspace
// Extends WorkspaceTool - has access to appType, toolSystem, fs, and git

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";
import { notifyWorkspaceServiceWorker } from "../../../../../../workspace/serviceWorkerBridge.js";

export class CreateFolderTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'createFolderTool';
  }

  /**
   * Create directory(ies)
   * @param {string|string[]} path - Path(s) to the directory(ies) to create
   * @param {boolean} [createParents=true] - Create parent directories if needed
   * @param {boolean} [ignoreExisting=true] - Don't error if directory already exists
   * @returns {Promise<Object>} Result with operation status
   */
  async createFolder(
    path,
    createParents = true,
    ignoreExisting = true
  ) {
    try {
      // Handle both single path and array of paths
      const paths = Array.isArray(path) ? path : [path];
      const results = [];
      const errors = [];

      // Validate paths
      if (!paths.length) {
        return this.returnError({}, "At least one valid directory path required");
      }

      for (const singlePath of paths) {
        if (!singlePath || typeof singlePath !== "string") {
          errors.push({ path: singlePath, error: "Invalid directory path" });
          continue;
        }

        // Route based on app type
        if (this.appType === "browser") {
          const result = await this.createFolderForBrowser(
            singlePath,
            createParents,
            ignoreExisting
          );

          if (result.success) {
            results.push({
              path: singlePath,
              action: result.data.action,
              success: true
            });
          } else {
            errors.push({
              path: singlePath,
              error: result.message,
              success: false
            });
          }
        } else if (this.appType === "local") {
          const result = await this.createFolderForLocal(
            singlePath,
            createParents,
            ignoreExisting
          );

          if (result.success) {
            results.push({
              path: singlePath,
              action: result.data.action,
              success: true
            });
          } else {
            errors.push({
              path: singlePath,
              error: result.message,
              success: false
            });
          }
        } else {
          errors.push({
            path: singlePath,
            error: `App type '${this.appType}' not yet implemented for createFolder`,
            success: false
          });
        }
      }

      // Return summary result
      const totalProcessed = results.length + errors.length;
      const successful = results.length;

      if (errors.length === 0) {
        return this.returnSuccess(
          {
            paths: results,
            totalProcessed,
            successful,
            failed: 0,
          },
          `Successfully created ${successful} director${successful === 1 ? 'y' : 'ies'}`
        );
      } else if (results.length === 0) {
        return this.returnError(
          {
            paths: errors,
            totalProcessed,
            successful: 0,
            failed: errors.length,
          },
          `Failed to create any directories`
        );
      } else {
        return this.returnSuccess(
          {
            paths: results,
            errors: errors,
            totalProcessed,
            successful,
            failed: errors.length,
          },
          `Created ${successful} director${successful === 1 ? 'y' : 'ies'}, ${errors.length} failed`
        );
      }
    } catch (error) {
      logger.error(
        {
          tags: "tools|createfolder|error",
          color1: "red",
        },
        `Failed to create folder(s): ${Array.isArray(path) ? path.join(', ') : path}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Create directory for browser apps using Lightning FS
   */
  async createFolderForBrowser(path, createParents, ignoreExisting) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    // Normalize path - Lightning FS uses absolute paths
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    // Prepend /files/ to the filesystem path
    const filesystemPath = `/files${normalizedPath}`;

    // Check if directory already exists
    let directoryExists = false;
    try {
      const stats = await fs.stat(filesystemPath);
      const isDir =
        typeof stats.isDirectory === "function"
          ? stats.isDirectory()
          : stats.type === "dir";

      if (isDir) {
        directoryExists = true;
        if (!ignoreExisting) {
          return this.returnError({}, `Directory already exists: ${path}`);
        }
      } else {
        return this.returnError({}, `Path exists but is not a directory: ${path}`);
      }
    } catch (error) {
      if (error.code !== "ENOENT") {
        throw error;
      }
      // Directory doesn't exist, which is fine for creation
    }

    // If directory already exists and we're ignoring existing, return success
    if (directoryExists) {
      logger.log(
        {
          tags: "tools|createfolder|info",
          color1: "lime",
        },
        `Directory already exists: ${filesystemPath}`
      );

      return this.returnSuccess(
        {
          path: normalizedPath,
          action: "exists",
        },
        "Directory already exists"
      );
    }

    // Ensure parent directory exists if needed
    if (createParents) {
      const dirPath = filesystemPath.split("/").slice(0, -1).join("/") || "/";
      if (dirPath && dirPath !== "/") {
        await this.ensureDirectory(dirPath);
      }
    }

    // Create the directory
    await fs.mkdir(filesystemPath);

    // Notify service worker that folder was created
    const appId = this.workspaceManager?.app?.id;
    if (appId) {
      try {
        await notifyWorkspaceServiceWorker({
          type: "WORKSPACE_FILE_UPDATED",
          appId: appId,
          filePath: normalizedPath,
        });
      } catch (error) {
        // Don't fail folder creation if notification fails
        logger.warn(
          {
            tags: "tools|createfolder|notification",
            color1: "yellow",
          },
          "Failed to notify service worker of folder creation",
          error
        );
      }
    }

    // Sync changes with workspace manager to update UI
    if (this.workspaceManager?.syncOpenFiles) {
      await this.workspaceManager.syncOpenFiles([normalizedPath]);
    }

    logger.log(
      {
        tags: "tools|createfolder|info",
        color1: "lime",
      },
      `Directory created: ${filesystemPath}`
    );

    return this.returnSuccess(
      {
        path: normalizedPath,
        action: "created",
      },
      "Directory created successfully"
    );
  }

  /**
   * Create directory for local apps (Revolt Desktop + workspaceLocalAppAPI).
   */
  async createFolderForLocal(path, createParents, ignoreExisting) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.createFolder) {
      return this.returnError(
        {},
        "Local folder creation requires Revolt Desktop (workspaceLocalAppAPI.createFolder)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const normalizedPath = this.normalizeAndValidatePath(path);
    if (!normalizedPath) {
      return this.returnError({}, "Invalid directory path");
    }
    const relativePath = normalizedPath.replace(/^\/+/, "");

    const res = await api.createFolder({
      rootPath: root,
      relativePath,
      createParents,
      ignoreExisting,
    });

    if (!res?.success) {
      return this.returnError({}, res?.error || "Failed to create directory on disk");
    }

    if (this.workspaceManager?.syncOpenFiles) {
      await this.workspaceManager.syncOpenFiles([normalizedPath]);
    }

    logger.log(
      {
        tags: "tools|createfolder|info",
        color1: "lime",
      },
      `Directory ${res.action === "exists" ? "already exists" : "created"} (local): ${normalizedPath}`
    );

    return this.returnSuccess(
      {
        path: normalizedPath,
        action: res.action || "created",
      },
      res.action === "exists" ? "Directory already exists" : "Directory created successfully"
    );
  }

  /**
   * Ensure directory exists, creating parent directories as needed
   */
  async ensureDirectory(directoryPath) {
    if (!directoryPath || directoryPath === "/") {
      return;
    }

    const fs = this.fs?.promises;
    if (!fs) {
      return;
    }

    const segments = directoryPath.split("/").filter(Boolean);
    let currentPath = "";

    for (const segment of segments) {
      currentPath += `/${segment}`;
      try {
        await fs.stat(currentPath);
      } catch (error) {
        if (error.code === "ENOENT") {
          try {
            await fs.mkdir(currentPath);
          } catch (mkdirError) {
            // Directory might have been created in parallel; ignore
            if (mkdirError.code !== "EEXIST") {
              logger.warn(
                {
                  tags: "tools|createfolder|mkdir",
                  color1: "yellow",
                },
                `Failed to create directory: ${currentPath}`,
                mkdirError
              );
            }
          }
        } else {
          throw error;
        }
      }
    }
  }
}